/**
 * Receipt photos in History: a thumbnail on the row, and a full-screen viewer.
 * Photos are private (rule 6): each opens through a signed link that lasts 15 minutes, fetched
 * when needed and reused for 12 minutes so scrolling History doesn't ask again and again.
 */
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useEffect, useState } from 'react';

import { api } from '../api/index.ts';
import type { Entry } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { Icon } from '../ui/icons.tsx';
import { Num, Spinner } from '../ui/parts.tsx';

const REUSE_MS = 12 * 60_000;
const links = new Map<string, { url: string; at: number }>();

/** The photo's signed link: null while loading, '' if it could not be fetched. */
function useReceiptLink(receiptId: string, entityId: string): string | null {
  const cached = links.get(receiptId);
  const fresh = cached && Date.now() - cached.at < REUSE_MS ? cached.url : null;
  const [url, setUrl] = useState<string | null>(fresh);
  useEffect(() => {
    if (fresh) {
      setUrl(fresh);
      return;
    }
    let live = true;
    setUrl(null);
    api
      .receiptUrl(receiptId, entityId)
      .then((u) => {
        links.set(receiptId, { url: u, at: Date.now() });
        if (live) setUrl(u);
      })
      .catch(() => live && setUrl(''));
    return () => {
      live = false;
    };
  }, [receiptId, entityId, fresh]);
  return url;
}

/** Row thumbnail: the first photo, with "+n" when there are more. */
export function ReceiptThumb({ entry, onOpen }: { entry: Entry; onOpen: () => void }) {
  const { t } = useT();
  const ids = entry.receiptIds ?? [];
  const first = ids[0] ?? '';
  const url = useReceiptLink(first, entry.entityId);
  const [broken, setBroken] = useState(false);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${t('history.photo')}: ${entry.itemName}`}
      className="relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border-[1.5px] border-line-strong bg-ground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
    >
      {url && !broken ? (
        <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} className="size-full object-cover" />
      ) : url === null ? (
        <span className="size-full animate-pulse bg-line" />
      ) : (
        <Icon name="camera" size={20} />
      )}
      {ids.length > 1 && (
        <span className="num absolute end-0.5 bottom-0.5 rounded-md bg-ink/85 px-1 text-[11px] leading-4 font-bold text-white">
          +{ids.length - 1}
        </span>
      )}
    </button>
  );
}

/** Full-screen photos of one entry. */
export function ReceiptViewer({ entry, onClose }: { entry: Entry | null; onClose: () => void }) {
  const { t } = useT();
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [entry?.id]);
  const ids = entry?.receiptIds ?? [];
  const count = ids.length;
  const go = (step: 1 | -1) => setIndex((i) => (i + step + count) % count);

  return (
    <Dialog open={entry !== null && count > 0} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/90" aria-hidden="true" />
      <DialogPanel
        className="safe-top safe-bottom fixed inset-0 flex flex-col text-white"
        onKeyDown={(e) => {
          if (count < 2) return;
          // Arrow keys follow the screen, whatever the language direction.
          if (e.key === 'ArrowRight') go(document.documentElement.dir === 'rtl' ? -1 : 1);
          if (e.key === 'ArrowLeft') go(document.documentElement.dir === 'rtl' ? 1 : -1);
        }}
      >
        <div className="flex min-h-16 shrink-0 items-center gap-2 px-2">
          <div className="flex min-w-0 flex-1 flex-col ps-2 leading-tight">
            <DialogTitle className="m-0 truncate text-[17px] font-bold">{entry?.itemName}</DialogTitle>
            <span className="text-[13px] text-white/75">
              {count > 1 ? <Num>{t('history.photoOf', { n: index + 1, count })}</Num> : t('history.photo')}
            </span>
          </div>
          <button
            type="button"
            aria-label={t('add.close')}
            onClick={onClose}
            className="flex size-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus-visible:outline-3 focus-visible:outline-white"
          >
            <Icon name="close" size={24} />
          </button>
        </div>

        <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-3">
          {entry && ids[index] && <BigPhoto key={ids[index]} receiptId={ids[index]} entityId={entry.entityId} />}
          {count > 1 && (
            <>
              <button
                type="button"
                aria-label={t('history.photoPrev')}
                onClick={() => go(-1)}
                className="absolute start-2 top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 hover:bg-black/75 focus-visible:outline-3 focus-visible:outline-white"
              >
                <Icon name="chevronStart" size={26} flip />
              </button>
              <button
                type="button"
                aria-label={t('history.photoNext')}
                onClick={() => go(1)}
                className="absolute end-2 top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 hover:bg-black/75 focus-visible:outline-3 focus-visible:outline-white"
              >
                <Icon name="chevronEnd" size={26} flip />
              </button>
            </>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}

function BigPhoto({ receiptId, entityId }: { receiptId: string; entityId: string }) {
  const { t } = useT();
  const url = useReceiptLink(receiptId, entityId);
  const [broken, setBroken] = useState(false);
  if (url === null) return <Spinner size={36} className="text-white" />;
  if (!url || broken) {
    return (
      <p className="m-0 flex flex-col items-center gap-3 px-6 text-center text-[15px] text-white/80 rtl:text-[17px]">
        <Icon name="cloudOff" size={36} />
        {navigator.onLine ? t('history.photoFailed') : t('history.photoOffline')}
      </p>
    );
  }
  return (
    <img src={url} alt={t('history.photo')} onError={() => setBroken(true)} className="max-h-full max-w-full rounded-lg object-contain" />
  );
}
